/**
 * SHA-256 hashing utility for generating cache keys
 *
 * This module provides cryptographic hash generation using the Web Crypto API
 * for creating consistent, collision-resistant cache keys from user prompts.
 *
 * @module utils/hash
 */

/**
 * Generates a SHA-256 hash-based cache key from a prompt and asset type
 *
 * The cache key is generated by:
 * 1. Normalizing the prompt (lowercase, trimmed)
 * 2. Combining prompt with asset type: "prompt:assetType"
 * 3. Generating SHA-256 hash using Web Crypto API
 * 4. Converting hash to 64-character hex string
 *
 * @param {string} prompt - User input prompt (e.g., "dragon")
 * @param {string} assetType - Type of asset (e.g., "character", "obstacle", "background")
 * @returns {Promise<string>} 64-character hex string SHA-256 hash
 * @throws {Error} If Web Crypto API is unavailable
 * @throws {Error} If hash generation fails
 *
 * @example
 * ```typescript
 * const key = await generateCacheKey("dragon", "character");
 * // Returns: "a7b3c5d4e6f8..." (64 hex chars)
 * ```
 */
export async function generateCacheKey(
  prompt: string,
  assetType: string
): Promise<string> {
  // Check for Web Crypto API availability
  if (!crypto || !crypto.subtle) {
    throw new Error('Web Crypto API not available');
  }

  try {
    // Normalize prompt: lowercase and trim whitespace for consistency
    const normalizedPrompt = prompt.toLowerCase().trim();

    // Combine prompt with asset type to create unique key
    const text = `${normalizedPrompt}:${assetType}`;

    // Encode text to Uint8Array for crypto API
    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    // Generate SHA-256 hash
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');

    return hashHex;
  } catch (error) {
    // Wrap crypto errors with more context
    throw new Error(
      `Failed to generate cache key: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}
